import { RoleCombobox } from '@/components/ui/Combobox'
import { useSession } from '@/hooks/useSession'
import { useSubscription } from '@/hooks/useSubscription'
import { JOB_DESCRIPTION_MAX_CHARS, JOB_DESCRIPTION_MAX_FILE_BYTES } from '@/lib/constants'
import { ACCEPTED_EXTENSIONS, extractTextFromFile, isAcceptedFileType } from '@/lib/extractFileText'
import { cn } from '@/lib/utils'
import type { Difficulty, SessionConfig } from '@/types/interview'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

interface DifficultyOption {
  value: Difficulty
  label: string
  description: string
}

const DIFFICULTIES: DifficultyOption[] = [
  { value: 'entry', label: 'Entry Level', description: 'Internships & first jobs' },
  { value: 'mid', label: 'Mid Level', description: '2–5 years of experience' },
  { value: 'senior', label: 'Senior Level', description: '5+ years of experience' },
]

export default function InterviewSetupPage() {
  const navigate = useNavigate()
  const { isPro, sessionsRemaining, isAtLimit, loading: subLoading, profile } = useSubscription()
  const { startSession, phase, sessionId, currentQuestion, error } = useSession()

  const [targetRole, setTargetRole] = useState('')
  const [difficulty, setDifficulty] = useState<Difficulty>('entry')
  const [jobDescription, setJobDescription] = useState('')
  const [uploadedFileName, setUploadedFileName] = useState<string | null>(null)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [wasTruncated, setWasTruncated] = useState(false)
  const [isExtracting, setIsExtracting] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Pre-fill role from profile
  useEffect(() => {
    if (profile?.targetRole) setTargetRole(profile.targetRole)
  }, [profile?.targetRole])

  // Navigate once session starts — the session page rehydrates from the server
  useEffect(() => {
    if (phase === 'questioning' && sessionId && currentQuestion) {
      navigate(`/interview/session/${sessionId}`, { replace: true })
    }
  }, [phase, sessionId, currentQuestion, navigate])

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return

    setUploadError(null)
    setWasTruncated(false)
    if (!isAcceptedFileType(file)) {
      setUploadError(`Unsupported file type. Use ${ACCEPTED_EXTENSIONS.join(', ')}.`)
      e.target.value = ''
      return
    }

    // Checked before extraction — a large PDF is otherwise fully parsed in the
    // browser and held in state before anything looks at its size.
    if (file.size > JOB_DESCRIPTION_MAX_FILE_BYTES) {
      setUploadError(
        `That file is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is ${JOB_DESCRIPTION_MAX_FILE_BYTES / 1024 / 1024} MB. Paste the text instead.`
      )
      e.target.value = ''
      return
    }

    setIsExtracting(true)
    try {
      const text = await extractTextFromFile(file)
      setJobDescription(text.slice(0, JOB_DESCRIPTION_MAX_CHARS))
      setWasTruncated(text.length > JOB_DESCRIPTION_MAX_CHARS)
      setUploadedFileName(file.name)
    } catch {
      setUploadError('Could not read the file. Try pasting the text directly.')
    } finally {
      setIsExtracting(false)
      e.target.value = ''
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const config: SessionConfig = {
      targetRole: targetRole.trim(),
      difficulty,
      ...(jobDescription.trim() ? { jobDescription: jobDescription.trim() } : {}),
    }
    await startSession(config)
  }

  const isSubmitting = phase === 'loading'

  if (subLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </main>
    )
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center p-8">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="space-y-1">
          <Link
            to="/dashboard"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Dashboard
          </Link>
          <h1 className="text-2xl font-bold tracking-tight">Set up your interview</h1>
          {!isPro && (
            <p className="text-sm text-muted-foreground">
              {isAtLimit
                ? 'You have used all free sessions this month.'
                : `${sessionsRemaining} free session${sessionsRemaining === 1 ? '' : 's'} remaining this month.`}
            </p>
          )}
        </div>

        {/* Upgrade gate */}
        {isAtLimit ? (
          <div className="rounded-xl border border-border bg-muted/40 p-6 space-y-4 text-center">
            <p className="text-sm font-medium">Upgrade to Pro for unlimited practice sessions.</p>
            <Link
              to="/settings"
              className="inline-flex items-center justify-center rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
            >
              Upgrade to Pro
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Target role */}
            <div className="space-y-1.5">
              <label htmlFor="targetRole" className="text-sm font-medium">
                Target role
              </label>
              <RoleCombobox
                id="targetRole"
                value={targetRole}
                onChange={setTargetRole}
                placeholder="Search roles — e.g. Product Manager"
              />
            </div>

            {/* AI question selection callout */}
            <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
              Let's practice with 7-10 questions - covering the basics (Tell me about yourself,
              strengths, failures) plus role-specific behavioral questions.
            </div>

            {/* Difficulty */}
            <fieldset className="space-y-2">
              <legend className="text-sm font-medium">Experience level</legend>
              <div className="flex flex-col gap-2">
                {DIFFICULTIES.map(({ value, label, description }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setDifficulty(value)}
                    className={cn(
                      'flex items-center justify-between rounded-lg border px-4 py-3 text-sm transition-colors text-left',
                      difficulty === value
                        ? 'border-primary bg-primary/10'
                        : 'border-border bg-background hover:bg-muted'
                    )}
                  >
                    <span className={cn('font-medium', difficulty === value && 'text-primary')}>
                      {label}
                    </span>
                    <span className="text-muted-foreground text-xs">{description}</span>
                  </button>
                ))}
              </div>
            </fieldset>

            {/* Job description */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label htmlFor="jobDescription" className="text-sm font-medium">
                  Job description{' '}
                  <span className="text-muted-foreground font-normal">— optional</span>
                </label>
                <span className="text-xs text-muted-foreground">Personalizes your questions</span>
              </div>
              {uploadedFileName && (
                <p className="text-xs text-muted-foreground">
                  Loaded from <span className="font-medium">{uploadedFileName}</span>{' '}
                  <button
                    type="button"
                    className="text-destructive hover:underline"
                    onClick={() => {
                      setUploadedFileName(null)
                      setJobDescription('')
                      setWasTruncated(false)
                    }}
                  >
                    Remove
                  </button>
                </p>
              )}
              {wasTruncated && (
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  That file was longer than the limit — only the first{' '}
                  {JOB_DESCRIPTION_MAX_CHARS.toLocaleString()} characters were kept. Edit below if
                  the important parts were cut.
                </p>
              )}
              <textarea
                id="jobDescription"
                value={jobDescription}
                maxLength={JOB_DESCRIPTION_MAX_CHARS}
                onChange={(e) => {
                  setJobDescription(e.target.value)
                  setWasTruncated(false)
                  if (uploadedFileName) setUploadedFileName(null)
                }}
                placeholder="Paste the job description here, or upload a file below…"
                rows={6}
                className="w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".txt,.pdf,.docx"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                  <button
                    type="button"
                    disabled={isExtracting}
                    onClick={() => fileInputRef.current?.click()}
                    className="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors disabled:opacity-50"
                  >
                    {isExtracting ? 'Reading file…' : 'Upload file'}
                  </button>
                  <span className="text-xs text-muted-foreground">.txt, .pdf, .docx</span>
                </div>
                <span
                  className={cn(
                    'text-xs tabular-nums',
                    jobDescription.length >= JOB_DESCRIPTION_MAX_CHARS
                      ? 'text-destructive'
                      : 'text-muted-foreground'
                  )}
                >
                  {jobDescription.length.toLocaleString()} /{' '}
                  {JOB_DESCRIPTION_MAX_CHARS.toLocaleString()}
                </span>
              </div>
              {uploadError && (
                <p role="alert" className="text-xs text-destructive">
                  {uploadError}
                </p>
              )}
            </div>

            {/* Error */}
            {error && (
              <p
                role="alert"
                className="rounded-lg bg-destructive/10 px-3 py-2 text-sm text-destructive"
              >
                {error}
              </p>
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={isSubmitting || !targetRole.trim()}
              className="w-full rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
            >
              {isSubmitting ? 'Generating questions…' : 'Start interview'}
            </button>
          </form>
        )}
      </div>
    </main>
  )
}
